import { cn } from '../lib/utils.js';

/* ===========================================================================
 * THE REQUIRED MARKER.
 *
 * 48 controls across 19 files carry `required`, and exactly 7 visible markers
 * existed app-wide — so 41 fields were mandatory and never said so until the
 * form refused to submit. Of the three renderings that did exist, only one was
 * right, and it was a private helper inside a 1500-line page component
 * (`apps/web/src/routes/employees/EmployeeFormPage.tsx`). This is that one,
 * promoted verbatim.
 *
 * `ms-*` NOT `ml-*`. The marker follows the label text, and under `dir="rtl"`
 * "following" is on the left. A physical margin puts an 8px gap on the wrong
 * side of the asterisk in Arabic — which is half this app's traffic.
 *
 * `aria-hidden` BECAUSE THE MARK IS NOT THE MESSAGE. A screen reader that meets
 * this element with no `aria-hidden` announces "star" — or, on some
 * verbosity settings, "asterisk" — jammed onto the end of the field's
 * accessible name ("Emirates ID star"). It is a visual convention only; the
 * machine-readable half of "this field is required" belongs on the CONTROL, as
 * `required` / `aria-required`, which is where assistive tech already looks and
 * where the browser's own validation reads it from.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS IS BOTH A COMPONENT AND A `<Label required>` PROP.
 *
 * The prop is the ergonomic path and covers the 48: a field's label is already
 * a `<Label>`, so `required` is one word at the call site, and the marker's
 * spacing, colour and `aria-hidden` cannot drift because no call site ever
 * types them. That is the entire reason three renderings existed.
 *
 * The component exists because the marker outlives the `<Label>`. A required
 * CHECKBOX GROUP is marked on its `<legend>`, a required section on a heading,
 * and a form's "* required" legend needs the same glyph in running text — none
 * of those are a `<Label>`, and without an export each would invent its fourth
 * copy. So: the component is the primitive, the prop is the sugar over it.
 * ======================================================================== */
export interface RequiredMarkProps {
  /** Extra classes. The marker is 12 characters of DOM; this is here for the
   *  rare caller that needs to nudge it, not for re-colouring it. */
  className?: string;
}

export function RequiredMark({ className }: RequiredMarkProps): JSX.Element {
  return (
    <span className={cn('ms-0.5 text-danger', className)} aria-hidden>
      *
    </span>
  );
}
